import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, TrendingUp, X,  ArrowRight } from "lucide-react";

import Card from "../../Card/Card";
import Container from "../../Container/Container";
import MediaTile from "../../MediaTile/MediaTile";
import SectionTitle from "../../SectionTitle/SectionTitle";
import Button from "../../Button/Button";
import { fadeUp, staggerContainer, viewportOnce } from "../../../Utils/animations";
import { caseStudies } from "../../../Data/caseStudiesData";

import "../../../Style/home/caseStudies.css";

function CaseStudies() {
  const [selectedStudy, setSelectedStudy] = useState(null);

  // Lock background scroll when reading a case study
  useEffect(() => {
    if (selectedStudy) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedStudy]);

  // Close on Escape key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setSelectedStudy(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleOpenStudy = (e, study) => {
    e.preventDefault();
    setSelectedStudy(study);
  };

  const handleGetSimilarResults = () => {
    setSelectedStudy(null);
    window.history.pushState({}, "", "/contact");
    window.dispatchEvent(new Event("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section className="section section--plain case-studies">
      <Container>
        <SectionTitle
          eyebrow="Case Studies"
          title="Results our clients"
          highlight="can point to."
        />

        <motion.div
          className="card-grid card-grid--3"
          variants={staggerContainer(0.08)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {caseStudies.map((study) => (
            <Card key={study.id} interactive gradientBorder variants={fadeUp()} className="case-studies__card">
              <MediaTile tone={study.tone} icon={<TrendingUp size={22} />} />
              <span className="case-studies__industry">{study.industry}</span>
              <h3 className="card__title mt-3">{study.title}</h3>
              <p className="card__text">{study.description}</p>

              <div className="case-studies__results">
                {study.results.map((result) => (
                  <div key={result.label}>
                    <strong>{result.value}</strong>
                    <span>{result.label}</span>
                  </div>
                ))}
              </div>

              <a
                href="#case-study"
                onClick={(e) => handleOpenStudy(e, study)}
                className="link-arrow case-studies__link"
              >
                View Case Study <ArrowUpRight size={16} />
              </a>
            </Card>
          ))}
        </motion.div>
      </Container>

      {/* Case Study Details Modal */}
      <AnimatePresence>
        {selectedStudy && (
          <div className="reader-backdrop" onClick={() => setSelectedStudy(null)}>
            <motion.div
              className="reader-modal"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 30, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.25 }}
            >
              <div className="reader-modal__header">
                <div className="reader-modal__meta">
                  <span className="reader-modal__tag">{selectedStudy.industry}</span>
                  <span className="reader-modal__date">Verified Client Engagement</span>
                </div>
                <button
                  type="button"
                  className="reader-modal__close-btn"
                  onClick={() => setSelectedStudy(null)}
                  aria-label="Close case study"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="reader-modal__scroll">
                <h2 className="reader-modal__title">{selectedStudy.title}</h2>
                <p className="reader-p reader-p--lead">{selectedStudy.description}</p>

                {/* Outcome Metrics Grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                    gap: "16px",
                    margin: "24px 0",
                  }}
                >
                  {selectedStudy.results.map((res) => (
                    <div
                      key={res.label}
                      style={{
                        padding: "16px",
                        borderRadius: "14px",
                        background: "var(--bg-secondary, #f8fafc)",
                        border: "1px solid var(--border-color, rgba(226, 232, 240, 0.8))",
                        textAlign: "center",
                      }}
                    >
                      <div style={{ fontSize: "1.75rem", fontWeight: 800, color: "#0284c7" }}>
                        {res.value}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "var(--text-secondary, #64748b)", marginTop: "4px" }}>
                        {res.label}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="reader-modal__content">
                  <div className="reader-point">
                    <div className="reader-point__badge">1</div>
                    <div className="reader-point__text">
                      <h5>The Operational Bottleneck</h5>
                      <p>
                        Prior to partnering with Conectify Tech, the client experienced heavy database decay, high bounce rates across cold outreach, and sales reps spending 35% of their working hours manually hunting for contact details.
                      </p>
                    </div>
                  </div>

                  <div className="reader-point">
                    <div className="reader-point__badge">2</div>
                    <div className="reader-point__text">
                      <h5>The Conectify Tech Solution</h5>
                      <p>
                        We deployed a custom multi-source enrichment pipeline. Every lead was scrubbed through triple-verification checks (SMTP handshake, active LinkedIn cross-referencing, and direct dial validation) tailored for their ideal customer profile (ICP).
                      </p>
                    </div>
                  </div>

                  <div className="reader-point">
                    <div className="reader-point__badge">3</div>
                    <div className="reader-point__text">
                      <h5>The Measurable Impact</h5>
                      <p>
                        Outbound SDR efficiency doubled within 60 days, email deliverability stabilized at 98.7%, and the client closed enterprise deals with an accelerated sales cycle.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="reader-modal__footer" style={{ marginTop: "32px" }}>
                  <div className="reader-footer-copy">
                    <h6>Want similar results for your business?</h6>
                    <p>Get a custom pipeline audit and target account sample list.</p>
                  </div>
                  <Button icon={<ArrowRight size={16} />} onClick={handleGetSimilarResults}>
                    Request Pipeline Audit
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
}

export default CaseStudies;